import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Upload,
  Trash2,
  FileSpreadsheet,
  Check,
  X,
  Download,
  RefreshCw,
  Table,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table as UITable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRICING_TABLES = [
  {
    key: "ultracolour_max",
    name: "UltraColour Max Pricing",
    columns: ["MinQty", "MaxQty", "SizeTier", "BaseCost"],
    description: "Pricing for UltraColour Max prints by quantity and size tier",
  },
  {
    key: "ultracolour_pro",
    name: "UltraColour Pro Pricing",
    columns: ["MinQty", "MaxQty", "SizeTier", "Standard", "Dye", "Sublimation", "Softshell"],
    description: "Pricing for UltraColour Pro prints with blocker options",
  },
  {
    key: "screen_print_pro_sheet",
    name: "Screen Print Pro Sheet Pricing",
    columns: ["MinSheets", "MaxSheets", "EffectiveColours", "IsWhiteOrBlack", "CostPerSheet"],
    description: "Sheet pricing by colour count and ink type",
  },
  {
    key: "screen_setup",
    name: "Screen Setup Pricing",
    columns: ["SetupPerColour"],
    description: "Setup cost per colour for screen printing",
  },
  {
    key: "embroidery_tier",
    name: "Embroidery Tier Pricing",
    columns: ["MinQty", "MaxQty", "BasePrice", "Per1000Stitches"],
    description: "Embroidery pricing by quantity tier",
  },
  {
    key: "embroidery_setup",
    name: "Embroidery Setup Pricing",
    columns: ["Setup"],
    description: "One-time embroidery setup cost",
  },
  {
    key: "dtg",
    name: "DTG Pricing",
    columns: ["MinQty", "MaxQty", "SizeTier", "CostPerPrint", "SetupCost"],
    description: "Direct-to-garment printing pricing by quantity and size",
  },
];

export default function PricingTables() {
  const [tableData, setTableData] = useState({});
  const [tableCounts, setTableCounts] = useState({});
  const [activeTab, setActiveTab] = useState("ultracolour_max");
  const [isLoading, setIsLoading] = useState(false);
  const [uploading, setUploading] = useState({});
  const fileInputRefs = useRef({});

  useEffect(() => {
    loadAllTables();
  }, []);

  const loadAllTables = async () => {
    setIsLoading(true);
    try {
      // Get counts
      const countsRes = await axios.get(`${API}/pricing`);
      setTableCounts(countsRes.data);

      // Load data for active tab
      await loadTableData(activeTab);
    } catch (error) {
      toast.error("Failed to load pricing tables");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTableData = async (tableName) => {
    try {
      const res = await axios.get(`${API}/pricing/${tableName}`);
      setTableData((prev) => ({
        ...prev,
        [tableName]: res.data.data,
      }));
    } catch (error) {
      console.error(`Failed to load ${tableName}`, error);
    }
  };

  const handleTabChange = async (value) => {
    setActiveTab(value);
    if (!tableData[value]) {
      await loadTableData(value);
    }
  };

  const handleFileUpload = async (tableName, file) => {
    if (!file) return;

    setUploading((prev) => ({ ...prev, [tableName]: true }));

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axios.post(`${API}/pricing/${tableName}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${res.data.rows} rows to ${tableName}`);
      await loadTableData(tableName);
      const countsRes = await axios.get(`${API}/pricing`);
      setTableCounts(countsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
      console.error(error);
    } finally {
      setUploading((prev) => ({ ...prev, [tableName]: false }));
      // Reset file input
      if (fileInputRefs.current[tableName]) {
        fileInputRefs.current[tableName].value = "";
      }
    }
  };

  const handleClearTable = async (tableName) => {
    if (!window.confirm(`Are you sure you want to clear all data from ${tableName}?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/pricing/${tableName}`);
      toast.success(`Cleared ${tableName}`);
      setTableData((prev) => ({ ...prev, [tableName]: [] }));
      setTableCounts((prev) => ({ ...prev, [tableName]: 0 }));
    } catch (error) {
      toast.error("Failed to clear table");
      console.error(error);
    }
  };

  const generateSampleCSV = (table) => {
    const headers = table.columns.join(",");
    let sampleData = "";
    
    // Generate sample rows based on table type
    if (table.key === "ultracolour_max") {
      sampleData = `1,9,A4,5.50\n10,24,A4,4.75\n25,49,A4,4.25\n50,99,A4,3.85\n100,999999,A4,3.50`;
    } else if (table.key === "ultracolour_pro") {
      sampleData = `20,49,A4,4.20,4.80,5.10,5.40\n50,99,A4,3.80,4.30,4.60,4.90\n100,999999,A4,3.40,3.90,4.20,4.50`;
    } else if (table.key === "screen_print_pro_sheet") {
      sampleData = `20,49,1,false,2.80\n20,49,2,false,3.20\n50,99,1,false,2.50\n50,99,2,false,2.90`;
    } else if (table.key === "screen_setup") {
      sampleData = `45`;
    } else if (table.key === "embroidery_tier") {
      sampleData = `1,24,8.50,0.85\n25,49,7.50,0.75\n50,99,6.50,0.65`;
    } else if (table.key === "embroidery_setup") {
      sampleData = `65`;
    }

    return `${headers}\n${sampleData}`;
  };

  const downloadSampleCSV = (table) => {
    const csv = generateSampleCSV(table);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${table.key}_sample.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const activeTable = PRICING_TABLES.find((t) => t.key === activeTab);
  const activeData = tableData[activeTab] || [];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-30">
        <div className="max-w-screen-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/settings">
              <Button variant="ghost" size="icon" data-testid="back-to-settings">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <FileSpreadsheet className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight">Pricing Tables</h1>
                <p className="text-xs text-muted-foreground">
                  Upload and manage pricing data
                </p>
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={loadAllTables}
            disabled={isLoading}
            data-testid="refresh-tables-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
            Refresh All
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-screen-2xl mx-auto p-6">
        {/* Status Overview */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {PRICING_TABLES.map((table) => (
            <Card
              key={table.key}
              className={`cursor-pointer transition-all ${
                activeTab === table.key ? "ring-2 ring-accent" : ""
              }`}
              onClick={() => handleTabChange(table.key)}
              data-testid={`table-card-${table.key}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Table className="w-4 h-4 text-muted-foreground" />
                  {tableCounts[table.key] > 0 ? (
                    <Badge variant="default" className="bg-green-600">
                      <Check className="w-3 h-3 mr-1" />
                      {tableCounts[table.key]}
                    </Badge>
                  ) : (
                    <Badge variant="secondary">
                      <X className="w-3 h-3 mr-1" />
                      Empty
                    </Badge>
                  )}
                </div>
                <p className="text-xs font-medium truncate">{table.name.replace(" Pricing", "")}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Active Table Detail */}
        {activeTable && (
          <Card data-testid={`active-table-${activeTab}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="section-header">{activeTable.name}</CardTitle>
                  <CardDescription>{activeTable.description}</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadSampleCSV(activeTable)}
                    data-testid={`download-sample-${activeTab}`}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Sample CSV
                  </Button>
                  <input
                    type="file"
                    accept=".csv"
                    ref={(el) => (fileInputRefs.current[activeTab] = el)}
                    onChange={(e) => handleFileUpload(activeTab, e.target.files?.[0])}
                    className="hidden"
                    data-testid={`file-input-${activeTab}`}
                  />
                  <Button
                    size="sm"
                    onClick={() => fileInputRefs.current[activeTab]?.click()}
                    disabled={uploading[activeTab]}
                    data-testid={`upload-btn-${activeTab}`}
                  >
                    <Upload className={`w-4 h-4 mr-2 ${uploading[activeTab] ? "animate-pulse" : ""}`} />
                    {uploading[activeTab] ? "Uploading..." : "Upload CSV"}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleClearTable(activeTab)}
                    disabled={!activeData.length}
                    data-testid={`clear-btn-${activeTab}`}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Expected Columns */}
              <div className="mb-4 p-3 bg-secondary/30 rounded-lg">
                <p className="text-xs text-muted-foreground mb-2">Expected CSV columns:</p>
                <div className="flex flex-wrap gap-2">
                  {activeTable.columns.map((col) => (
                    <Badge key={col} variant="outline" className="font-mono text-xs">
                      {col}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Data Table */}
              {activeData.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No data uploaded yet</p>
                  <p className="text-xs mt-1">Upload a CSV file to populate this table</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px] border rounded-lg">
                  <UITable>
                    <TableHeader className="sticky top-0 bg-secondary">
                      <TableRow>
                        {activeTable.columns.map((col) => (
                          <TableHead key={col} className="font-mono text-xs uppercase">
                            {col}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activeData.map((row, index) => (
                        <TableRow key={index} className="table-row-hover">
                          {activeTable.columns.map((col) => (
                            <TableCell key={col} className="font-mono text-sm">
                              {typeof row[col] === "boolean"
                                ? row[col]
                                  ? "Yes"
                                  : "No"
                                : row[col] ?? "-"}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </UITable>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        )}

        {/* Instructions Card */}
        <Card className="mt-6 bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <h3 className="font-medium text-blue-800 mb-3">CSV Upload Instructions</h3>
            <ul className="text-sm text-blue-700 space-y-2">
              <li>1. Download the sample CSV to see the expected format</li>
              <li>2. Fill in your pricing data (numeric values only, no currency symbols)</li>
              <li>3. Upload the CSV file - existing data will be replaced</li>
              <li>4. Boolean columns should use: true/false, yes/no, or 1/0</li>
              <li>5. Use 999999 for MaxQty when there's no upper limit</li>
            </ul>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
